(() => {
  'use strict';
  if (window.BCMJourney) return;
  const text = window.BCMJourneyText || {};
  const sizeKey = 'bcm.journey.size.v1';
  const normalSize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '').replace('california', 'cal');
  const storage = {
    get(key) { try { return sessionStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch {} }
  };
  const rememberSize = value => { if (value && value !== 'all') storage.set(sizeKey, value); };
  const savedSize = fallback => storage.get(sizeKey) || fallback;
  const records = new WeakMap();
  function readCard(element) {
    if (records.has(element)) return records.get(element);
    const script = element.querySelector('[data-bcm-journey-product]');
    if (!script) return null;
    try {
      const data = JSON.parse(script.textContent);
      const record = { element, data, variant: null };
      records.set(element, record);
      return record;
    } catch { return null; }
  }
  function chooseVariant(data, size) {
    return data.variants.filter(v => size === 'all' || normalSize(v.size) === normalSize(size))
      .reduce((best, variant) => !best || variant.price < best.price ? variant : best, null);
  }
  function updateCard(element, size) {
    const record = readCard(element);
    if (!record) return null;
    const variant = chooseVariant(record.data, size);
    record.variant = variant;
    element.dataset.price = variant ? variant.price : '';
    element.dataset.bcmSizeMissing = String(!variant);
    const price = element.querySelector('.product-card-info .price');
    const saleBadge = element.querySelector('.badge.onsale');
    if (saleBadge) {
      saleBadge.hidden = !variant || !variant.available || variant.compare <= variant.price;
      if (!saleBadge.hidden) saleBadge.textContent = `${text.save} ${variant.saving}`;
    }
    if (price && variant) price.textContent = `${size === 'all' ? text.from : size} ${variant.money}`;
    element.querySelectorAll('a[href]').forEach(link => {
      try {
        const url = new URL(link.href, location.origin);
        if (url.pathname.split('/products/')[1] !== record.data.url.split('/products/')[1]) return;
        if (variant) url.searchParams.set('variant', variant.id);
        link.href = url.pathname + url.search + url.hash;
      } catch {}
    });
    return record;
  }
  function restore(root, controls, key) {
    try {
      const state = JSON.parse(storage.get(key) || '{}');
      controls.forEach((control, index) => {
        const value = state[index];
        if (Array.from(control.options).some(o => o.value === value)) control.value = value;
      });
      return Object.keys(state).length > 0;
    } catch { return false; }
  }
  function persist(controls, key) { storage.set(key, JSON.stringify(controls.map(c => c.value))); }
  function setupCompare(root, cards, getSize) {
    const panel = root.querySelector('[data-bcm-compare]');
    if (!panel) return () => {};
    const selected = new Set();
    const selectionKey = `bcm.compare.v1:${location.pathname}`;
    const checkboxes = new Map();
    const focusCard = card => {
      if (!card.hidden) checkboxes.get(card)?.focus();
      else root.querySelector('[data-bcm-size]')?.focus();
    };
    const status = panel.querySelector('[data-bcm-compare-status]');
    const content = panel.querySelector('[data-bcm-compare-content]');
    const create = (tag, value) => { const node = document.createElement(tag); node.textContent = value; return node; };
    const update = () => {
      content.replaceChildren();
      panel.hidden = selected.size === 0;
      status.textContent = text.compare_count?.replace('[count]', selected.size) || '';
      const grid = document.createElement('div');
      grid.className = 'bcm-journey-compare-grid';
      selected.forEach(card => {
        const record = updateCard(card, getSize());
        const data = record.data;
        const article = document.createElement('article');
        article.append(create('h4', data.title));
        const dl = document.createElement('dl');
        const facts = [
          [text.price, record.variant ? `${record.variant.title}: ${record.variant.money}` : text.size_missing],
          [text.listed_feel, data.feel || text.confirm_fact],
          [text.construction, data.construction || text.confirm_fact],
          [text.cooling, data.cooling || text.confirm_fact],
          [text.height, data.height ? `${data.height} ${text.inches}` : text.confirm_fact],
          [text.display_heading, data.showroom],
          [text.trial, data.trial],
          [text.purchase, record.variant?.available ? text.purchase_available : text.purchase_confirm]
        ];
        facts.forEach(([label, value]) => dl.append(create('dt', label), create('dd', value)));
        article.append(dl);
        const link = create('a', text.view_product);
        link.href = record.variant ? `${data.url}?variant=${record.variant.id}` : data.url;
        article.append(link);
        const remove = create('button', text.remove);
        remove.type = 'button';
        remove.addEventListener('click', () => {
          selected.delete(card);
          checkboxes.get(card).checked = false;
          update();
          focusCard(card);
        });
        article.append(remove);
        grid.append(article);
      });
      content.append(grid);
      storage.set(selectionKey, JSON.stringify(Array.from(selected).map(card => readCard(card).data.id)));
      checkboxes.forEach((checkbox, card) => { checkbox.disabled = selected.size >= 3 && !selected.has(card); });
    };
    cards.forEach(card => {
      const record = readCard(card);
      const host = card.querySelector('.product-card-info');
      if (!record || !host) return;
      const label = document.createElement('label');
      label.className = 'bcm-journey-compare-toggle';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.setAttribute('aria-label', `${text.compare_action}: ${record.data.title}`);
      checkbox.addEventListener('change', () => {
        if (checkbox.checked && selected.size < 3) selected.add(card);
        else { selected.delete(card); checkbox.checked = false; }
        update();
      });
      label.append(checkbox, create('span', text.compare_action));
      host.append(label);
      checkboxes.set(card, checkbox);
    });
    panel.querySelector('[data-bcm-compare-clear]').addEventListener('click', () => {
      selected.clear(); checkboxes.forEach(c => { c.checked = false; }); update();
      checkboxes.values().next().value?.focus();
    });
    try {
      const saved = JSON.parse(storage.get(selectionKey) || '[]');
      if (Array.isArray(saved)) cards.forEach(card => {
        const record = readCard(card);
        if (record && checkboxes.has(card) && saved.map(String).includes(String(record.data.id)) && selected.size < 3) { selected.add(card); checkboxes.get(card).checked = true; }
      });
    } catch {}
    return update;
  }
  function productSelection() {
    const premium = document.querySelector('[data-mattress-variants]');
    const standard = document.querySelector('variant-selects[data-update-url="true"]');
    let control;
    if (premium) {
      const data = JSON.parse(premium.textContent);
      control = document.querySelector(`.bcm-pdp input[data-option-position="${data.sizePosition}"]:checked`);
    } else if (standard) {
      const fieldset = standard.querySelector('fieldset[data-handle="size"]');
      control = fieldset?.querySelector('input:checked, select');
    }
    return control;
  }
  function syncProductSize() {
    if (location.pathname.endsWith('/estate-soft-mattress')) return;
    const premium = document.querySelector('.bcm-pdp[data-bcm-explicit-variant]');
    if (premium ? premium.dataset.bcmExplicitVariant === 'true' : new URL(location.href).searchParams.has('variant')) return;
    const saved = savedSize('');
    if (!saved) return;
    const current = productSelection();
    if (!current || normalSize(current.value) === normalSize(saved)) return;
    const fieldset = current.closest('fieldset');
    if (current.tagName === 'SELECT') {
      const option = Array.from(current.options).find(o => normalSize(o.value) === normalSize(saved));
      if (!option) return;
      current.value = option.value;
      current.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const input = Array.from(fieldset.querySelectorAll('input')).find(i => normalSize(i.value) === normalSize(saved));
      if (!input) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  window.BCMJourney = { normalSize, savedSize, rememberSize, updateCard, readCard, chooseVariant, setupCompare, restore, persist };
  const boot = () => {
    syncProductSize();
    document.addEventListener('change', event => {
      if (event.target.closest('variant-selects, .bcm-pdp__sizes')) {
        const selected = productSelection();
        if (selected) rememberSize(selected.value);
      }
    });
    document.dispatchEvent(new Event('bcm:journey-ready'));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
